import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { Textarea } from '../../../components/ui/Textarea';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/Dialog';
import { useContracts } from '../../../core/hooks/useContracts';
import { useServices } from '../../../core/hooks/useServices';
import { useClients } from '../../../core/hooks/useClients';
import { useOrganizations } from '../../../core/hooks/useOrganizations';
import { useTasks } from '../../../core/hooks/useTasks';
import { useUsers } from '../../../core/hooks/useUsers';
import { useAuth } from '../../../core/contexts/AuthContext';
import type { TimeEntry } from '../../../core/models/TimeEntry';
import { TimeEntryStatus, TIME_ENTRY_TYPES } from '../../../core/models/TimeEntry';
import { UserRole } from '../../../core/models/User';
import { differenceInCalendarDays, format } from 'date-fns';

interface TimeEntryFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<TimeEntry, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  initialData?: Partial<TimeEntry>;
}

function parseDateInput(value: string | undefined, fallback: Date): Date {
  if (!value) return new Date(fallback.getFullYear(), fallback.getMonth(), fallback.getDate());
  const [y, m, d] = value.split('-').map((x) => Number(x));
  if (!y || !m || !d) return new Date(fallback.getFullYear(), fallback.getMonth(), fallback.getDate());
  return new Date(y, m - 1, d);
}

function splitDurationForDates(start: Date, end: Date, totalMinutes: number | undefined) {
  const safeMinutes = Math.max(0, Math.floor(totalMinutes || 0));
  const daySpan = Math.max(0, differenceInCalendarDays(end, start));
  const dayMinutes = daySpan * 24 * 60;
  const remainder = Math.max(0, safeMinutes - dayMinutes);
  const hours = Math.floor(remainder / 60);
  const minutes = remainder % 60;
  return { hours, minutes };
}

function roundDurationToQuarterMinutes(totalMinutes: number): number {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return 0;
  const rounded = Math.round(totalMinutes / 15) * 15;
  return rounded > 0 ? rounded : 15;
}

export const TimeEntryForm: React.FC<TimeEntryFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
}) => {
  const { organizationId, currentUser } = useAuth();
  const { organizations } = useOrganizations();
  const [formOrgId, setFormOrgId] = useState(organizationId || '');
  const effectiveOrgId = currentUser?.role === UserRole.SUPER_ADMIN ? formOrgId : (organizationId || '');
  const { clients } = useClients(effectiveOrgId || null);
  const { tasks } = useTasks(effectiveOrgId || null);
  const canPickAttributedUser =
    currentUser?.role === UserRole.SUPER_ADMIN || currentUser?.role === UserRole.CLIENT_ADMIN;
  const { users: orgUsers } = useUsers(effectiveOrgId || undefined);

  const attributedUserOptions = useMemo(() => {
    const byUid = new Map<string, { uid: string; label: string }>();
    for (const u of orgUsers) {
      byUid.set(u.uid, {
        uid: u.uid,
        label: u.displayName?.trim() || u.email || u.uid,
      });
    }
    if (currentUser?.uid && !byUid.has(currentUser.uid)) {
      byUid.set(currentUser.uid, {
        uid: currentUser.uid,
        label: currentUser.displayName?.trim() || currentUser.email || currentUser.uid,
      });
    }
    return [...byUid.values()];
  }, [orgUsers, currentUser?.uid, currentUser?.displayName, currentUser?.email]);
  const { contracts: orgContracts } = useContracts(null, effectiveOrgId || undefined);
  const { services } = useServices(effectiveOrgId || undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const today = useMemo(() => new Date(), []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      organizationId: initialData?.organizationId || initialData?.clientId || organizationId || '',
      projectId: initialData?.projectId || '',
      taskId: initialData?.taskId || '',
      taskTitle: initialData?.taskTitle || '',
      type: initialData?.type || '',
      contractId: initialData?.contractId || '',
      serviceId: initialData?.serviceId || '',
      description: initialData?.description || '',
      startDate: initialData?.startTime
        ? format(initialData.startTime, 'yyyy-MM-dd')
        : format(today, 'yyyy-MM-dd'),
      endDate: initialData?.endTime
        ? format(initialData.endTime, 'yyyy-MM-dd')
        : format(today, 'yyyy-MM-dd'),
      durationHours: initialData?.startTime && initialData?.endTime
        ? splitDurationForDates(initialData.startTime, initialData.endTime, initialData.duration).hours
        : 0,
      durationMinutes: initialData?.startTime && initialData?.endTime
        ? splitDurationForDates(initialData.startTime, initialData.endTime, initialData.duration).minutes
        : 0,
      billable: initialData?.billable ?? true,
      status: initialData?.status || TimeEntryStatus.PENDING,
      attributedUserId: initialData?.userId || currentUser?.uid || '',
    },
  });

  const projectId = watch('projectId') || '';
  const selectedProjectId = projectId || '';
  const billable = watch('billable');
  const durationMinutes = Number(watch('durationMinutes') || 0);
  const contracts = selectedProjectId
    ? orgContracts.filter((c) => c.clientId === selectedProjectId || c.clientId === effectiveOrgId)
    : orgContracts;
  const availableTasks = useMemo(() => {
    if (!selectedProjectId) return [];
    return tasks.filter((task) => task.projectId === selectedProjectId || task.clientId === selectedProjectId);
  }, [tasks, selectedProjectId]);

  useEffect(() => {
    if (open && initialData) {
      const parentId = initialData.clientId || initialData.organizationId || organizationId || '';
      const legacyProject =
        initialData.projectId ||
        (initialData.clientId &&
        initialData.organizationId &&
        initialData.clientId !== initialData.organizationId
          ? initialData.clientId
          : '');
      setValue('organizationId', parentId);
      setValue('projectId', legacyProject || '');
      setValue('taskId', initialData.taskId || '');
      setValue('taskTitle', initialData.taskTitle || '');
      setValue('type', initialData.type || '');
      setValue('contractId', initialData.contractId || '');
      setValue('serviceId', initialData.serviceId || '');
      setValue('description', initialData.description || '');
      const startDate = initialData.startTime || today;
      const endDate = initialData.endTime || today;
      const roundedDuration = roundDurationToQuarterMinutes(initialData.duration || 0);
      const durationParts = splitDurationForDates(startDate, endDate, roundedDuration);
      setValue('startDate', format(startDate, 'yyyy-MM-dd'));
      setValue('endDate', format(endDate, 'yyyy-MM-dd'));
      setValue('durationHours', durationParts.hours);
      setValue('durationMinutes', durationParts.minutes);
      setValue('billable', initialData.billable ?? true);
      setValue('status', initialData.status || TimeEntryStatus.PENDING);
      setValue('attributedUserId', initialData.userId || currentUser?.uid || '');
      if (currentUser?.role === UserRole.SUPER_ADMIN && parentId) {
        setFormOrgId(parentId);
      }
    } else if (open && !initialData) {
      setValue('attributedUserId', currentUser?.uid || '');
      setValue('startDate', format(today, 'yyyy-MM-dd'));
      setValue('endDate', format(today, 'yyyy-MM-dd'));
      setValue('durationHours', 0);
      setValue('durationMinutes', 0);
      setValue('taskId', '');
      setValue('taskTitle', '');
      setValue('projectId', '');
    }
  }, [open, initialData, organizationId, currentUser?.role, currentUser?.uid, setValue, today]);


  const onFormSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      const startDate = parseDateInput(data.startDate, today);
      const endDate = parseDateInput(data.endDate, today);
      if (endDate < startDate) {
        alert('End date must be on or after start date');
        setIsSubmitting(false);
        return;
      }

      const hours = Math.max(0, Math.floor(Number(data.durationHours) || 0));
      const minutes = Number(data.durationMinutes) || 0;
      if (![0, 15, 30, 45].includes(minutes)) {
        alert('Minutes must be in 15-minute increments');
        setIsSubmitting(false);
        return;
      }
      const daySpan = Math.max(0, differenceInCalendarDays(endDate, startDate));
      const duration = daySpan * 24 * 60 + (hours * 60) + minutes;
      const roundedDuration = roundDurationToQuarterMinutes(duration);
      if (roundedDuration <= 0) {
        alert('Duration must be greater than 0 minutes');
        setIsSubmitting(false);
        return;
      }

      const start = startDate;
      const end = new Date(start.getTime() + roundedDuration * 60_000);

      const effClientId = currentUser?.role === UserRole.SUPER_ADMIN ? (data.organizationId || formOrgId) : (organizationId || '');

      const attributedUid = canPickAttributedUser
        ? (data.attributedUserId || currentUser?.uid || '')
        : currentUser?.uid || '';

      const payload: Omit<TimeEntry, 'id' | 'createdAt' | 'updatedAt'> = {
        clientId: effClientId,
        organizationId: effClientId,
        userId: attributedUid,
        description: data.description,
        startTime: start,
        endTime: end,
        duration: roundedDuration,
        billable: data.billable,
        status: data.status,
      };
      if (data.projectId) payload.projectId = data.projectId;
      if (data.taskId) {
        payload.taskId = data.taskId;
        payload.taskTitle = data.taskTitle || undefined;
      }
      if (data.type) payload.type = data.type;
      if (data.contractId) payload.contractId = data.contractId;
      if (data.serviceId) payload.serviceId = data.serviceId;

      await onSubmit(payload);
      reset();
      onClose();
    } catch (error) {
      console.error('Error submitting time entry:', error);
      alert('Failed to save time entry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {initialData ? 'Edit Time Entry' : 'Create Time Entry'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          {currentUser?.role === UserRole.SUPER_ADMIN && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Client</label>
              <Select
                value={formOrgId}
                onValueChange={(v) => {
                  setFormOrgId(v);
                  setValue('organizationId', v);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {canPickAttributedUser && effectiveOrgId && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Attributed to</label>
              <Select
                value={watch('attributedUserId') || currentUser?.uid || ''}
                onValueChange={(v) => setValue('attributedUserId', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  {attributedUserOptions.map((u) => (
                    <SelectItem key={u.uid} value={u.uid}>
                      {u.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Project</label>
              <Select
                value={watch('projectId') || 'org'}
                onValueChange={(v) => {
                  setValue('projectId', v === 'org' ? '' : v);
                  setValue('taskId', '');
                  setValue('taskTitle', '');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Client general or specific project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="org">Client general</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Task (Optional)</label>
              <Select
                value={watch('taskId') || '__none__'}
                onValueChange={(v) => {
                  if (v === '__none__') {
                    setValue('taskId', '');
                    setValue('taskTitle', '');
                    return;
                  }
                  const task = availableTasks.find((item) => item.id === v);
                  setValue('taskId', v);
                  setValue('taskTitle', task?.title || '');
                }}
                disabled={!selectedProjectId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={selectedProjectId ? 'Select optional task' : 'Select a project first'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No task</SelectItem>
                  {availableTasks.map((task) => (
                    <SelectItem key={task.id} value={task.id}>
                      {task.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Type</label>
              <Select
                value={watch('type')}
                onValueChange={(v) => setValue('type', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_ENTRY_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Contract</label>
              <Select
                value={watch('contractId')}
                onValueChange={(value) => setValue('contractId', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Optional – select if applicable" />
                </SelectTrigger>
                <SelectContent>
                  {contracts.map((contract) => (
                    <SelectItem key={contract.id} value={contract.id}>
                      {contract.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Service</label>
              <Select
                value={watch('serviceId')}
                onValueChange={(value) => setValue('serviceId', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((service) => (
                    <SelectItem key={service.id} value={service.id}>
                      {service.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <Input
              label="Start Date"
              type="date"
              {...register('startDate')}
              error={errors.startDate?.message}
            />
            <Input
              label="End Date"
              type="date"
              {...register('endDate')}
              error={errors.endDate?.message}
            />
            <Input
              label="Hours"
              type="number"
              min={0}
              step={1}
              {...register('durationHours')}
            />
            <div className="space-y-2">
              <label className="text-sm font-medium">Minutes</label>
              <Select
                value={String(durationMinutes)}
                onValueChange={(v) => setValue('durationMinutes', Number(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0</SelectItem>
                  <SelectItem value="15">15</SelectItem>
                  <SelectItem value="30">30</SelectItem>
                  <SelectItem value="45">45</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <p className="text-xs text-gray-500">
            Use start/end dates plus hours/minutes. Dates default to today.
          </p>

          <Textarea
            label="Description"
            {...register('description', { required: 'Description is required' })}
            error={errors.description?.message}
            rows={3}
            placeholder="What did you work on?"
          />

          <div className="flex items-center space-x-2">
            <Checkbox
              checked={billable}
              onCheckedChange={(checked) => setValue('billable', checked === true)}
            />
            <label className="text-sm font-medium">Billable</label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {initialData ? 'Update Entry' : 'Create Entry'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
